import React, { useEffect, useState } from 'react';
import {
  FaArrowDown,
  FaArrowUp,
  FaEdit,
  FaPaperPlane,
  FaTrash,
} from 'react-icons/fa';
import { LuBookmark } from 'react-icons/lu';
import { MdAdd } from 'react-icons/md';
import { ThemedSpinner } from '../ThemedSpinner';
import { useTranslation } from '../../localization/useAppTranslation';
import type { SavedPrompt } from '../../types/preferences';
import { sortSavedPromptsByOrder } from '../../utils/savedPrompts';

interface SavedPromptsPanelProps {
  prompts: SavedPrompt[] | null;
  isLoading: boolean;
  loadError: string | null;
  onRetryLoad: () => Promise<SavedPrompt[] | null>;
  rowActionStates?: Readonly<Record<string, SavedPromptRowActionState>>;
  canSendPrompt?: boolean;
  onCreatePrompt?: () => void;
  onEditPrompt?: (prompt: SavedPrompt) => void;
  onDeletePrompt?: (prompt: SavedPrompt) => void;
  onMovePrompt?: (prompt: SavedPrompt, direction: 'up' | 'down') => void;
  onUsePrompt?: (prompt: SavedPrompt) => void;
}

export interface SavedPromptRowActionState {
  isMutating: boolean;
  error: string | null;
}

export const SavedPromptsPanel: React.FC<SavedPromptsPanelProps> = ({
  prompts,
  isLoading,
  loadError,
  onRetryLoad,
  rowActionStates = {},
  canSendPrompt = true,
  onCreatePrompt,
  onEditPrompt,
  onDeletePrompt,
  onMovePrompt,
  onUsePrompt,
}) => {
  const { t } = useTranslation('preferences');
  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null);

  useEffect(() => {
    if (!prompts?.some((prompt) => prompt.id === selectedPromptId)) {
      setSelectedPromptId(sortSavedPromptsByOrder(prompts || [])[0]?.id || null);
    }
  }, [prompts, selectedPromptId]);

  if (prompts === null) {
    if (loadError) {
      return (
        <div className="saved-prompts-state saved-prompts-error" role="alert">
          <p>{loadError}</p>
          <button type="button" className="saved-prompts-retry-button" onClick={() => void onRetryLoad()}>
            {t('common:actions.retry')}
          </button>
        </div>
      );
    }

    return (
      <div
        className="saved-prompts-state"
        role="status"
        aria-live="polite"
        aria-busy={isLoading}
      >
        <ThemedSpinner size="md" ariaLabel={t('savedPrompts.loading')} />
      </div>
    );
  }

  const sortedPrompts = sortSavedPromptsByOrder(prompts);
  const selectedPrompt = sortedPrompts.find((prompt) => prompt.id === selectedPromptId) || null;
  const selectedPromptIndex = selectedPrompt
    ? sortedPrompts.findIndex((prompt) => prompt.id === selectedPrompt.id)
    : -1;
  const isSelectedPromptMutating = selectedPrompt
    ? rowActionStates[selectedPrompt.id]?.isMutating === true
    : false;
  const canEditSelectedPrompt = Boolean(selectedPrompt && onEditPrompt && !isSelectedPromptMutating);
  const canDeleteSelectedPrompt = Boolean(selectedPrompt && onDeletePrompt && !isSelectedPromptMutating);
  const canMoveSelectedPromptUp = Boolean(
    selectedPrompt && onMovePrompt && !isSelectedPromptMutating && selectedPromptIndex > 0,
  );
  const canMoveSelectedPromptDown = Boolean(
    selectedPrompt
    && onMovePrompt
    && !isSelectedPromptMutating
    && selectedPromptIndex < sortedPrompts.length - 1,
  );
  const canUseSelectedPrompt = Boolean(
    selectedPrompt && onUsePrompt && canSendPrompt && !isSelectedPromptMutating,
  );

  return (
    <section
      className="saved-prompts-panel"
      aria-label={t('savedPrompts.ariaLabel')}
    >
      <div className="saved-prompts-toolbar">
        <button
          type="button"
          className="saved-prompts-icon-button"
          aria-label={t('savedPrompts.actions.new')}
          title={t('savedPrompts.actions.new')}
          disabled={!onCreatePrompt}
          onClick={onCreatePrompt}
        >
          <MdAdd aria-hidden="true" />
        </button>
        <button
          type="button"
          className="saved-prompts-icon-button"
          aria-label={t('savedPrompts.actions.edit')}
          title={t('savedPrompts.actions.edit')}
          disabled={!canEditSelectedPrompt}
          onClick={() => selectedPrompt && onEditPrompt?.(selectedPrompt)}
        >
          <FaEdit aria-hidden="true" />
        </button>
        <button
          type="button"
          className="saved-prompts-icon-button"
          aria-label={t('savedPrompts.actions.delete')}
          title={t('savedPrompts.actions.delete')}
          disabled={!canDeleteSelectedPrompt}
          onClick={() => selectedPrompt && onDeletePrompt?.(selectedPrompt)}
        >
          <FaTrash aria-hidden="true" />
        </button>
        <button
          type="button"
          className="saved-prompts-icon-button"
          aria-label={t('savedPrompts.actions.moveUp')}
          title={t('savedPrompts.actions.moveUp')}
          disabled={!canMoveSelectedPromptUp}
          onClick={() => selectedPrompt && onMovePrompt?.(selectedPrompt, 'up')}
        >
          <FaArrowUp aria-hidden="true" />
        </button>
        <button
          type="button"
          className="saved-prompts-icon-button"
          aria-label={t('savedPrompts.actions.moveDown')}
          title={t('savedPrompts.actions.moveDown')}
          disabled={!canMoveSelectedPromptDown}
          onClick={() => selectedPrompt && onMovePrompt?.(selectedPrompt, 'down')}
        >
          <FaArrowDown aria-hidden="true" />
        </button>
        <span className="saved-prompts-toolbar-spacer" />
        <button
          type="button"
          className="saved-prompts-icon-button saved-prompts-send-button"
          aria-label={t('savedPrompts.actions.send')}
          title={t('savedPrompts.actions.send')}
          disabled={!canUseSelectedPrompt}
          onClick={() => selectedPrompt && onUsePrompt?.(selectedPrompt)}
        >
          <FaPaperPlane aria-hidden="true" />
        </button>
      </div>
      <div
        className="sidebar-panel-scroll saved-prompts-list"
        role="listbox"
        aria-label={t('savedPrompts.ariaLabel')}
      >
        {prompts.length === 0 && (
          <div className="saved-prompts-state">
            {t('savedPrompts.empty')}
          </div>
        )}
        {sortedPrompts.map((prompt) => (
          <button
            type="button"
            className={`saved-prompt-row ${selectedPrompt?.id === prompt.id ? 'is-selected' : ''} ${rowActionStates[prompt.id]?.isMutating ? 'is-mutating' : ''}`}
            key={prompt.id}
            role="option"
            aria-selected={selectedPrompt?.id === prompt.id}
            aria-label={prompt.title}
            onClick={() => setSelectedPromptId(prompt.id)}
            onDoubleClick={() => {
              if (canSendPrompt && onUsePrompt && !rowActionStates[prompt.id]?.isMutating) {
                onUsePrompt(prompt);
              }
            }}
          >
            <LuBookmark className="saved-prompt-row-icon" aria-hidden="true" />
            <span className="saved-prompt-row-copy">
              <span className="saved-prompt-row-title" title={prompt.title}>
                {prompt.title}
              </span>
              <span className="saved-prompt-row-description">
                {prompt.prompt_text}
              </span>
            </span>
          </button>
        ))}
        {selectedPrompt && rowActionStates[selectedPrompt.id]?.error && (
          <div className="saved-prompt-action-error" role="alert">
            {rowActionStates[selectedPrompt.id].error}
          </div>
        )}
      </div>
    </section>
  );
};